import { useCallback, useEffect, useState } from 'react'
import { X } from 'lucide-react'

import { Checkbox } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import { getVendorProducts, IVendorProduct } from '@/core/services/vendors'
import { CreateProductModal } from '@/home/components/vendors/CreateProductModal'
import Loader from '@/common/components/Loader'

interface ProductsTabProps {
  vendorId: string
  onError: (message: string) => void
  createModalOpen: boolean
  onCreateModalChange: (open: boolean) => void
}

export const ProductsTab = ({
  vendorId,
  onError,
  createModalOpen,
  onCreateModalChange,
}: ProductsTabProps) => {
  const [products, setProducts] = useState<IVendorProduct[]>([])
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [editProduct, setEditProduct] = useState<IVendorProduct | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const fetchProducts = useCallback(async () => {
    if (!vendorId) return
    setLoading(true)
    try {
      const { data } = await getVendorProducts(vendorId)
      setProducts(data)
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    } finally {
      setLoading(false)
    }
  }, [vendorId])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  const handleSelectAll = () => {
    setIsCheckAll(!isCheckAll)
    setIsCheck(!isCheckAll ? products.map((p) => p.id) : [])
  }

  const handleClick = (id: string, checked: boolean) => {
    setIsCheck(checked ? [...isCheck, id] : isCheck.filter((i) => i !== id))
  }

  const handleNameClick = (product: IVendorProduct) => {
    setEditProduct(product)
    setEditModalOpen(true)
  }

  const handleEditModalChange = (open: boolean) => {
    setEditModalOpen(open)
    if (!open) setEditProduct(null)
  }

  const handleSuccess = () => {
    setIsCheck([])
    setIsCheckAll(false)
    fetchProducts()
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox onCheckedChange={handleSelectAll} checked={isCheckAll} />
      ),
      accessor: '',
      render: (row: IVendorProduct) => (
        <Checkbox
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Image',
      accessor: 'photo',
      render: (row: IVendorProduct) =>
        row.imageLink ? (
          <img
            src={row.imageLink}
            alt={row.name}
            className="h-10 w-10 rounded-md object-cover"
          />
        ) : (
          <div className="h-10 w-10 rounded-md bg-monochrome-3-60" />
        ),
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: true,
      searchable: true,
      render: (row: IVendorProduct) => (
        <a
          className="text-accent-1 underline cursor-pointer"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            handleNameClick(row)
          }}
        >
          {row.name}
        </a>
      ),
    },
    { title: 'Price', accessor: 'price', sortable: true },
    {
      title: 'Tags',
      accessor: 'tags',
      render: (row: IVendorProduct) => <span>{row.tags.join(', ')}</span>,
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row: IVendorProduct) => (
        <a
          className="text-accent-1 underline cursor-pointer"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            handleNameClick(row)
          }}
        >
          Edit
        </a>
      ),
    },
    {
      title: '',
      accessor: '',
      render: (row: IVendorProduct) =>
        isCheck.includes(row.id) ? (
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded-full bg-monochrome-5/70 text-monochrome-1 hover:bg-red-500/30 hover:text-red-400 transition-colors cursor-pointer"
            onClick={() => handleNameClick(row)}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null,
    },
  ]

  return (
    <>
      {loading && <Loader />}
      {products.length ? (
        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={products}
          isCheck={isCheck}
        />
      ) : (
        <div className="flex items-center justify-center py-20 text-monochrome-2">
          No Products data available
        </div>
      )}

      <CreateProductModal
        open={createModalOpen}
        onOpenChange={onCreateModalChange}
        vendorId={vendorId}
        onSuccess={handleSuccess}
        onError={onError}
      />

      <CreateProductModal
        open={editModalOpen}
        onOpenChange={handleEditModalChange}
        vendorId={vendorId}
        product={editProduct}
        onSuccess={handleSuccess}
        onError={onError}
      />
    </>
  )
}
